import React, { useEffect, useState, useRef } from "react"
import "./AddForm.scss"
import FormApi from "Api/FormApi"
import { useNavigate } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Input, Label, Badge } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { Dropdown } from "primereact/dropdown"
import { Toast } from "primereact/toast"
import { Chips } from "primereact/chips"

const AddForm = () => {
  document.title = "Add Form"
  const navigate = useNavigate()
  const toast = useRef(null)
  const [loading, setLoading] = useState(false)
  const [institutions, setInstitutions] = useState([])

  const formInitialData = {
    institutionId: "",
    courseId: "",
    programId: "",
    formName: "",
    formDescription: "",
    fields: []
  }

  const [programs, setPrograms] = useState([])
  const [courses, setCourses] = useState([])
  const [selectedInstitution, setSelectedInstitution] = useState(null)

  const fieldInitialData = {
    fieldName: "",
    fieldType: "text",
    fieldPlaceHolder: "",
    sectionName: "",
    sectionPriority: 1,
    options: [],
    isRequired: false
  }

  const [formData, setFormData] = useState(formInitialData)
  const [currentField, setCurrentField] = useState(fieldInitialData)
  const [editingFieldIndex, setEditingFieldIndex] = useState(-1)

  const fieldTypes = [
    { label: "Text", value: "text" },
    { label: "Email", value: "email" },
    { label: "Number", value: "number" },
    { label: "Phone", value: "tel" },
    { label: "URL", value: "url" },
    { label: "Textarea", value: "textarea" },
    { label: "Select Dropdown", value: "select" },
    { label: "Radio Button", value: "radio" },
    { label: "Checkbox", value: "checkbox" },
    { label: "Date", value: "date" },
    { label: "Time", value: "time" },
    { label: "Date & Time", value: "datetime-local" },
    { label: "File Upload", value: "file" },
    { label: "Hidden", value: "hidden" }
  ]

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Forms", link: "/forms" },
    { label: "Add Form" }
  ]

  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  const accessToken = localStorage.getItem("accessToken")

  // Get institutions for dropdown
  const getInstitutions = () => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getInstitutionMenuList(config)
      .then(response => {
        if (response.data.status && response.data.institutions) {
          console.log("Sample institution object:", response.data.institutions[0])
          
          // Sort institutions by displayName for better UX
          const sortedInstitutions = response.data.institutions.sort((a, b) =>
            (a.displayName || a.businessName || "").localeCompare(b.displayName || b.businessName || "")
          )
          setInstitutions(sortedInstitutions)
        } else {
          setInstitutions([])
        }
      })
      .catch(error => {
        console.error("Error fetching institutions:", error)
        setInstitutions([])
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch institutions"
        })
      })
  }

  // Get programs for selected institution
  const getPrograms = (institutionCode) => {
    console.log("getPrograms called with code:", institutionCode)
    
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getProgramsByInstitution(institutionCode, config)
      .then(response => {
        console.log("Programs API response:", response.data)
        if (response.data.status) {
          setPrograms(response.data.programs || [])
        } else {
          setPrograms([])
          // Show the specific message from API response
          toast.current?.show({
            severity: "info",
            summary: "Info",
            detail: response.data.message || "No programs found for this institution",
            life: 3000
          })
        }
      })
      .catch(error => {
        console.error("Error fetching programs:", error)
        setPrograms([])
        // Show API error message if available, otherwise generic message
        const errorMessage = error.response?.data?.message || "Failed to fetch programs"
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000
        })
      })
  }

  // Get courses for selected program
  const getCourses = (programId) => {
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.getCoursesByProgram(programId, config)
      .then(response => {
        if (response.data.status) {
          setCourses(response.data.courses || [])
        } else {
          setCourses([])
          // Show the specific message from API response
          toast.current?.show({
            severity: "info",
            summary: "Info",
            detail: response.data.message || "No courses found for this program",
            life: 3000
          })
        }
      })
      .catch(error => {
        console.error("Error fetching courses:", error)
        setCourses([])
        // Show API error message if available, otherwise generic message
        const errorMessage = error.response?.data?.message || "Failed to fetch courses"
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000
        })
      })
  }

  useEffect(() => {
    getInstitutions()
  }, [accessToken])

  const handleFormDataChange = (name, value) => {
    setFormData({ ...formData, [name]: value })
    
    // Handle cascading dropdowns
    if (name === "institutionId") {
      // Reset program and course when institution changes
      setFormData(prev => ({ ...prev, [name]: value, programId: "", courseId: "" }))
      setPrograms([])
      setCourses([])
      
      // Find selected institution to get its code
      const institution = institutions.find(inst => inst.businessId === value)
      console.log("Selected institution:", institution)
      setSelectedInstitution(institution)
      
      if (institution) {
        // Use businessId as the institution code for the API call
        const institutionCode = institution.businessId
        console.log("Using institution code (businessId):", institutionCode)
        
        if (institutionCode) {
          getPrograms(institutionCode)
        } else {
          console.log("No businessId found")
        }
      }
    } else if (name === "programId") {
      // Reset course when program changes
      setFormData(prev => ({ ...prev, [name]: value, courseId: "" }))
      setCourses([])
      
      if (value) {
        getCourses(value)
      }
    }
  }

  const handleFieldChange = (name, value) => {
    setCurrentField({ ...currentField, [name]: value })
  }

  const addField = () => {
    if (!currentField.fieldName || !currentField.fieldType || !currentField.sectionName) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Field name, type, and section name are required"
      })
      return
    }

    if (editingFieldIndex >= 0) {
      // Update existing field
      const updatedFields = [...formData.fields]
      updatedFields[editingFieldIndex] = { ...currentField }
      setFormData({ ...formData, fields: updatedFields })
      setEditingFieldIndex(-1)
    } else {
      // Add new field
      setFormData({
        ...formData,
        fields: [...formData.fields, { ...currentField }]
      })
    }

    setCurrentField(fieldInitialData)
  }

  const editField = (index) => {
    setCurrentField({ ...formData.fields[index] })
    setEditingFieldIndex(index)
  }

  const removeField = (index) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index)
    setFormData({ ...formData, fields: updatedFields })

    if (editingFieldIndex === index) {
      setCurrentField(fieldInitialData)
      setEditingFieldIndex(-1)
    }
  }

  const cancelEdit = () => {
    setCurrentField(fieldInitialData)
    setEditingFieldIndex(-1)
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!formData.institutionId || !formData.programId || !formData.courseId || !formData.formName || formData.fields.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "Warning",
        detail: "Institution, program, course, form name, and at least one field are required"
      })
      return
    }

    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    FormApi.addForm(formData, config)
      .then(response => {
        if (response.data.status) {
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Form created successfully"
          })
          setTimeout(() => {
            navigate("/forms")
          }, 1500)
        }
      })
      .catch(error => {
        console.error("Error creating form:", error)
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Failed to create form"
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const needsOptions = ["select", "radio", "checkbox"].includes(currentField.fieldType)

  return (
    <>
      <Toast ref={toast} />
      <Row>
        <Col xs="12">
          <div className="form-header d-flex align-items-center justify-content-between">
            <CustomBreadCrumb
              data={breadcrumbItems}
              pageTitle="Add Form"
              handleClick={handleBreadcrumbClick}
            />
          </div>

          <form onSubmit={handleSubmit}>
            {/* Basic Form Information */}
            <Card className="mt-4">
              <CardBody>
                <h5 className="mb-4">Basic Information</h5>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label">Institution *</Label>
                      <Dropdown
                        value={formData.institutionId}
                        options={institutions || []}
                        onChange={(e) => handleFormDataChange("institutionId", e.value)}
                        optionLabel="displayName"
                        optionValue="businessId"
                        placeholder={institutions.length > 0 ? "Select Institution" : "Loading institutions..."}
                        className="w-100"
                        showClear
                        filter
                        filterBy="displayName,businessName"
                        emptyMessage="No institutions found"
                        disabled={institutions.length === 0}
                        virtualScrollerOptions={{ itemSize: 38 }}
                        maxSelectedLabels={1}
                        filterPlaceholder="Search institutions..."
                      />
                      {institutions.length === 0 && (
                        <small className="text-muted">Loading institutions...</small>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label">Form Name *</Label>
                      <InputText
                        value={formData.formName}
                        onChange={(e) => handleFormDataChange("formName", e.target.value)}
                        placeholder="Enter form name"
                        className="w-100"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label">Program *</Label>
                      <Dropdown
                        value={formData.programId}
                        options={programs || []}
                        onChange={(e) => handleFormDataChange("programId", e.value)}
                        optionLabel="programName"
                        optionValue="_id"
                        placeholder={
                          !formData.institutionId 
                            ? "Select Institution first" 
                            : programs.length > 0 
                              ? "Select Program" 
                              : "Loading programs..."
                        }
                        className="w-100"
                        showClear
                        disabled={!formData.institutionId || programs.length === 0}
                        filter
                        filterBy="programName"
                        emptyMessage="No programs found"
                      />
                      {formData.institutionId && programs.length === 0 && (
                        <small className="text-muted">Loading programs...</small>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <Label className="form-label">Course *</Label>
                      <Dropdown
                        value={formData.courseId}
                        options={courses || []}
                        onChange={(e) => handleFormDataChange("courseId", e.value)}
                        optionLabel="courseName"
                        optionValue="_id"
                        placeholder={
                          !formData.programId 
                            ? "Select Program first" 
                            : courses.length > 0 
                              ? "Select Course" 
                              : "Loading courses..."
                        }
                        className="w-100"
                        showClear
                        disabled={!formData.programId || courses.length === 0}
                        filter
                        filterBy="courseName"
                        emptyMessage="No courses found"
                      />
                      {formData.programId && courses.length === 0 && (
                        <small className="text-muted">Loading courses...</small>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="mb-3">
                      <Label className="form-label">Form Description</Label>
                      <InputTextarea
                        value={formData.formDescription}
                        onChange={(e) => handleFormDataChange("formDescription", e.target.value)}
                        placeholder="Enter form description"
                        rows={3}
                        className="w-100"
                      />
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Field Builder */}
            <Card className="mt-4">
              <CardBody>
                <h5 className="mb-4">Add Form Fields</h5>
                <Row>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">Field Name *</Label>
                      <InputText
                        value={currentField.fieldName}
                        onChange={(e) => handleFieldChange("fieldName", e.target.value)}
                        placeholder="Enter field name"
                        className="w-100"
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">Field Type *</Label>
                      <Dropdown
                        value={currentField.fieldType}
                        options={fieldTypes}
                        onChange={(e) => handleFieldChange("fieldType", e.value)}
                        placeholder="Select field type"
                        className="w-100"
                      />
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="mb-3">
                      <Label className="form-label">Placeholder</Label>
                      <InputText
                        value={currentField.fieldPlaceHolder}
                        onChange={(e) => handleFieldChange("fieldPlaceHolder", e.target.value)}
                        placeholder="Enter placeholder text"
                        className="w-100"
                      />
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={needsOptions ? 3 : 4}>
                    <div className="mb-3">
                      <Label className="form-label">Section Name *</Label>
                      <InputText
                        value={currentField.sectionName}
                        onChange={(e) => handleFieldChange("sectionName", e.target.value)}
                        placeholder="Enter section name"
                        className="w-100"
                      />
                    </div>
                  </Col>
                  <Col md={needsOptions ? 2 : 2}>
                    <div className="mb-3">
                      <Label className="form-label">Section Priority *</Label>
                      <InputText
                        type="number"
                        value={currentField.sectionPriority}
                        onChange={(e) => handleFieldChange("sectionPriority", parseInt(e.target.value) || 1)}
                        placeholder="1"
                        min="1"
                        className="w-100"
                      />
                      <small className="text-muted">Lower numbers appear first</small>
                    </div>
                  </Col>
                  {needsOptions && (
                    <Col md={4}>
                      <div className="mb-3">
                        <Label className="form-label">Options (for select/radio/checkbox)</Label>
                        <Chips
                          value={currentField.options}
                          onChange={(e) => handleFieldChange("options", e.value)}
                          placeholder="Type option and press Enter"
                          className="w-100"
                        />
                        <small className="text-muted">Press Enter after typing each option</small>
                      </div>
                    </Col>
                  )}
                  <Col md={needsOptions ? 3 : 6}>
                    <div className="mb-3">
                      <Label className="form-label">Required Field</Label>
                      <Dropdown
                        value={currentField.isRequired}
                        options={[
                          { label: "Optional", value: false },
                          { label: "Required", value: true }
                        ]}
                        onChange={(e) => handleFieldChange("isRequired", e.value)}
                        placeholder="Select requirement"
                        className="w-100"
                      />
                    </div>
                  </Col>
                </Row>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    color="primary"
                    onClick={addField}
                  >
                    {editingFieldIndex >= 0 ? "Update Field" : "Add Field"}
                  </Button>
                  {editingFieldIndex >= 0 && (
                    <Button
                      type="button"
                      color="secondary"
                      onClick={cancelEdit}
                    >
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>

            {/* Fields Preview */}
            {formData.fields.length > 0 && (
              <Card className="mt-4">
                <CardBody>
                  <h5 className="mb-4">Form Fields Preview ({formData.fields.length} fields)</h5>
                  <div className="fields-preview">
                    {formData.fields.map((field, index) => (
                      <Card key={index} className="mb-2 field-card">
                        <CardBody className="py-2">
                          <Row className="align-items-center">
                            <Col md={3}>
                              <strong>
                                {field.fieldName}
                                {field.isRequired && <span className="text-danger"> *</span>}
                              </strong>
                              <br />
                              <small className="text-muted">{field.fieldType}</small>
                            </Col>
                            <Col md={2}>
                              {field.fieldPlaceHolder && (
                                <small className="text-info">Placeholder: {field.fieldPlaceHolder}</small>
                              )}
                            </Col>
                            <Col md={2}>
                              {field.sectionName && (
                                <small className="text-success">
                                  Section: {field.sectionName}
                                  <br />
                                  Priority: {field.sectionPriority || 1}
                                </small>
                              )}
                            </Col>
                            <Col md={2}>
                              {field.options && field.options.length > 0 && (
                                <small className="text-warning">Options: {field.options.length}</small>
                              )}
                            </Col>
                            <Col md={1}>
                              {field.isRequired ? (
                                <Badge color="danger" className="badge-sm">Required</Badge>
                              ) : (
                                <Badge color="secondary" className="badge-sm">Optional</Badge>
                              )}
                            </Col>
                            <Col md={2} className="text-end">
                              <Button
                                size="sm"
                                color="info"
                                className="me-1"
                                onClick={() => editField(index)}
                              >
                                <i className="mdi mdi-pencil"></i>
                              </Button>
                              <Button
                                size="sm"
                                color="danger"
                                onClick={() => removeField(index)}
                              >
                                <i className="mdi mdi-delete"></i>
                              </Button>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </CardBody>
              </Card>
            )}

            {/* Submit Buttons */}
            <Card className="mt-4">
              <CardBody>
                <div className="d-flex gap-2">
                  <Button
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? "Creating..." : "Create Form"}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => navigate("/forms")}
                  >
                    Cancel
                  </Button>
                </div>
              </CardBody>
            </Card>
          </form>
        </Col>
      </Row>
    </>
  )
}

export default AddForm